<template>
  <div>
    <Header
      :projectName="projectName"
      :showProductTour="true"
      @start-product-tour="startProductTour()"
      @projectLogBtnClicked="projectLogBtnClicked"
    />
    <ProjectSearchDocs
      v-if="!dataLoading && searchID"
      :searchMode="searchMode"
      :organizedFilter="organizedFilter"
      :query="query"
      componentRole="search"
    />
    <ProjectSearchDocs
      v-else-if="!dataLoading"
      :searchMode="selectedSearchMode"
      :productsToSearch="productsToSearch"
      componentRole="project"
    />
    <Footer />
    <SnippetModal />
    <SaveSearch />
    <v-tour name="productTour" :steps="productTourSteps"></v-tour>
  </div>
</template>

<script>
import Header from '@/components/Header.vue';
import ProjectSearchDocs from '@/components/ProjectSearchDocs.vue';
import Footer from '@/components/Footer.vue';

import {
  getUserFirstVisitEntry,
  listUserProjects,
  getSearchResults,
} from '@/graphql/queries';
import { mapGetters } from 'vuex';
import { createUserFirstVisitEntry } from '@/graphql/mutations';

import { Auth, API, graphqlOperation } from 'aws-amplify';
import SnippetModal from '@/components/modals/SnippetModal';
import SaveSearch from '@/components/modals/SaveSearch';
import { isUserInProject } from '../utils/projectValidators';
import { getCurUser } from '../constants/fromLocalStorageConstants';
import { logError } from '@/utils/loggerUtils';

export default {
  name: 'ProjectSearchDocsDashboard',
  components: {
    SnippetModal,
    SaveSearch,
    Header,
    ProjectSearchDocs,
    Footer,
  },
  props: {
    id: String,
  },
  data() {
    return {
      dataLoading: true,
      projectName: '',
      selectedSearchMode: 'All Content',
      productsToSearch: [],
      filtersExpanded: false,
      searchID: null,
      searchMode: 'All Content',
      organizedFilter: null,
      query: null,
      ////////////////////////////////////////
      userinfo: {},
      isDev: process.env.VUE_APP_DEV === 'true',
      // product tour steps
      productTourSteps: [
        {
          target: '#search-modes',
          params: {
            placement: 'right-start',
            enableScrolling: false,
          },
          content:
            '<span class="font-weight-bold">Step 1: Choose Content Type</span><br/>What type of information are you searching for?<br/>Select a category to focus your search on content that is associated with your area of interest.<br/>Select “All Content" to search all content.',
        },
        {
          target: '#search-input',
          params: {
            placement: 'left-start',
            enableScrolling: false,
          },
          content:
            '<span class="font-weight-bold">Step 2: Type in Search Query</span><br/>Type in the keyword or combination of keywords that you are searching for.<br/>By default, spaces between keywords allow you to find pages with all the keywords.<ul><li>Quotation marks allow you to search for exact matches<br/>ex: <code>“primary endpoint”</code></li><li>The pipe sign between keywords allows to find pages with at least one of the keywords<br/>ex: <code>NSCLC|”non-small-cell lung cancer”</code></li><li>The minus sign allows you to exclude keywords<br/>ex: <code>-melanoma</code></li><li>Brackets allow you to combine multiple operators together. For example, to search for primary endpoints or outcome measures associated with NSCLC or non-small-cell lung cancer but not melanoma, type:<br/><code>(“primary endpoint”|”primary outcome measure”) (NSCLC|”non-small-cell lung cancer”)-melanoma</code></li></ul>',
        },
        {
          target: '#analytics-groups',
          params: {
            placement: 'left-start',
            enableScrolling: false,
          },
          before: () =>
            new Promise((resolve, reject) => {
              try {
                this.beforeStep('analytics-tab');
                resolve('analytics-tab');
              } catch (error) {
                logError(error);
                reject('analytics-tab');
              }
            }),
          content:
            '<span class="font-weight-bold">Step 3: Visualize Results</span><br/>Matched content is represented in graphs under the Analytics tab to help you navigate the results. Two sets of graphs are provided:<ul><li>the first representing the percentage of matched content over time, by product, therapeutic area, product class, indication and company, <li>and the second set of graphs represents the the number of products with matched content over time, by therapeutic area, by product class, by indication and by company.</li></ul><br/><br/>The graphs are interactive and can be used to apply filters. They can also be switched to table views and downloaded.',
        },
        {
          target: '#tab-group',
          params: {
            placement: 'bottom-start',
            enableScrolling: false,
          },
          before: () =>
            new Promise((resolve, reject) => {
              try {
                this.beforeStep('search-results-tab');
                resolve('search-results-tab');
              } catch (error) {
                logError(error);
                reject('search-results-tab');
              }
            }),
          content:
            '<span class="font-weight-bold">Step 4: View Documents/Pages</span><br/>Pages matching the search query are grouped by document and listed under the Search Results tab along with a snippet of the matched paragraph.<br/>Clicking on the document title will take you to the cover page of the document, and clicking on the page number will open the document on the matched page.',
        },
        {
          target: '#filter-tour',
          content:
            '<span class="font-weight-bold">Step 5: Filter Search Results</span><br/>To further reduce the number of matched results and eliminate any irrelevant documents, the Result Filters can be used to focus the search on documents associated with products, therapeutic areas, drug classes, indications, designations, applications types, document topics, agencies and more.',
          params: {
            placement: 'right-start',
            enableScrolling: false,
          },
        },
        {
          target: '#view-project-log-tour',
          content:
            '<span class="font-weight-bold">View Project Log</span><br/>You can view your project log, i.e., saved results, notes and bookmarks by clicking on "View Project Log".',
          params: {
            placement: 'right-start',
            enableScrolling: false,
          },
        },
        {
          target: '#all-projects-tour',
          content:
            '<span class="font-weight-bold">All Projects</span><br/>You can view all your projects by clicking on "All Projects".<br/>Here, you can create a new project or delete existing projects.</br>',
          params: {
            placement: 'right-start',
            enableScrolling: false,
          },
        },
      ],
    };
  },
  computed: {
    ...mapGetters(['project', 'projects']),
  },
  async created() {
    //If user clicks on the project list which means there is projectID query in the path
    let dataResponse;
    if (this.$route.query.projectID) {
      await this.$store.dispatch('FETCH_PROJECTS');

      this.$store.commit('SET_PROJECT', this.$route.query.projectID);
      dataResponse = await API.graphql(
        graphqlOperation(listUserProjects, {
          filter: {
            userID: { eq: getCurUser().email },
            projectID: { eq: this.$route.query.projectID },
          },
        })
      );

      const project = dataResponse.data.listUserProjects.items[0].project;
      if (!project || !isUserInProject(project, getCurUser().email)) {
        logError(
          new Error(
            `Bad project, redirected - User: ${getCurUser().email}, Project: ${
              project && project.id
            }`
          )
        );
        return this.$router.replace({
          path: '/project-list',
        });
      }

      const projectPreference = JSON.parse(project.projectPreference);
      this.projectName = project.name;
      this.selectedSearchMode =
        projectPreference.searchMode !== undefined
          ? projectPreference.searchMode
          : 'All Content';
      this.productsToSearch = projectPreference.products;
    } else if (!this.$route.query.searchID) {
      await this.$store.dispatch('FETCH_PROJECTS');
      // set first project to be default
      if (this.projects.length > 0) {
        this.$store.commit('SET_PROJECT', this.projects[0].project.id);
        dataResponse = await API.graphql(
          graphqlOperation(listUserProjects, {
            filter: {
              userID: { eq: getCurUser().email },
              projectID: { eq: this.projects[0].project.id },
            },
          })
        );
        const project = dataResponse.data.listUserProjects.items[0].project;
        this.projectName = project.name;
        this.$router.push({
          path: '/project-searchdocs-dashboard/',
          query: { projectID: this.projects[0].project.id },
        });
      } else {
        this.$router.push({
          path: '/',
        });
      }
    }

    if (this.$route.query.searchID) {
      this.searchID = this.$route.query.searchID;
      const searchResults = await API.graphql(
        graphqlOperation(getSearchResults, {
          id: this.searchID,
        })
      );
      const searchInfo = searchResults.data.getSearchResults;
      this.searchMode =
        searchInfo.mode !== undefined ? searchInfo.mode : 'All Content';
      this.organizedFilter = this.reorganizeFilters(
        JSON.parse(searchInfo.filters)
      );
      this.query = searchInfo.query;
    }
    this.dataLoading = false;
  },
  async mounted() {
    this.userinfo = await Auth.currentUserInfo();
    // only start product tour for users visiting current product version for the first time
    const response = await API.graphql(
      graphqlOperation(getUserFirstVisitEntry, {
        id:
          this.userinfo.attributes.email +
          '-' +
          (process.env.VUE_APP_PRODUCT_TOUR_VERSION &&
          process.env.VUE_APP_PRODUCT_TOUR_VERSION.length > 0
            ? process.env.VUE_APP_PRODUCT_TOUR_VERSION
            : '1.0'),
      })
    );
    if (
      response.data.getUserFirstVisitEntry === null ||
      !response.data.getUserFirstVisitEntry.first_visit
    ) {
      await API.graphql(
        graphqlOperation(createUserFirstVisitEntry, {
          input: {
            id:
              this.userinfo.attributes.email +
              '-' +
              (process.env.VUE_APP_PRODUCT_TOUR_VERSION &&
              process.env.VUE_APP_PRODUCT_TOUR_VERSION.length > 0
                ? process.env.VUE_APP_PRODUCT_TOUR_VERSION
                : '1.0'),
            username: this.userinfo.attributes.email,
            first_visit: true,
            product_version:
              process.env.VUE_APP_PRODUCT_TOUR_VERSION &&
              process.env.VUE_APP_PRODUCT_TOUR_VERSION.length > 0
                ? process.env.VUE_APP_PRODUCT_TOUR_VERSION
                : '1.0',
          },
        })
      );
      this.startProductTour();
    }
  },
  methods: {
    projectLogBtnClicked() {
      let id;
      if (this.$route.query.projectID) {
        id = this.$route.query.projectID;
      } else {
        id = JSON.parse(localStorage.getItem('project')).id;
      }

      const routeData = this.$router.resolve({
        path: `/${id}/saved-results`,
        params: { id: id },
      });
      window.open(routeData.href, '_blank');
    },
    async updateProjectInfo(params) {
      this.projectName = params[0];
      this.selectedSearchMode = params[1];
      this.productsToSearch = params[2];
      const projectPreference = {
        searchMode: params[1],
        products: params[2],
      };
      const payload = {
        id: this.id,
        name: params[0],
        projectPreference: JSON.stringify(projectPreference),
      };

      await this.$store.dispatch('UPDATE_PROJECT', payload);
    },
    reorganizeFilters(filters) {
      const organizedfilter = {};
      filters.forEach((filter) => {
        if (!(filter.name in organizedfilter)) {
          organizedfilter[filter.name] = [filter.val];
        } else {
          organizedfilter[filter.name].push(filter.val);
        }
      });
      return organizedfilter;
    },

    //////////////////////////////////////////
    async startProductTour() {
      this.$tours['productTour'].options.labels.buttonSkip = 'Skip Tour';
      this.$tours['productTour'].start();
    },
    beforeStep(stepName) {
      if (stepName === 'analytics-tab')
        document.getElementById('analytics-tab').click();
      if (stepName === 'search-results-tab')
        document.getElementById('search-results-tab').click();
    },
  },
};
</script>
<style scoped></style>
